import { useEffect, useState } from "react"
import { Skeleton } from "components/ui/skeleton/Skeleton"
import { TableExpandable } from "components/ui/tables/TableExpandable/TableExpandable"
import { useGetQuotations } from "hooks/api/quotation/quotations.hook"
import { useSearchParams } from "react-router-dom"
import { TableQuotationsColumns } from "./TableQuotationsColumns"
import { TableQuotationsExpandable } from "./TableQuotationsExpandable"
import { NoQuotations } from "./layout/NoQuotations/NoQuotations"
import type { TQuotationTableData } from "types/quotations.type"
import type { TStopPropagation } from "types/default.type"
import style from "./TableQuotations.module.scss"

const { tableQuotations } = style

type Props = {
	onDownload: (fileName: string, downloadLink: string, type: string, e?: TStopPropagation) => void
	onPreview: (downloadLink: string) => void
	createQuotation: () => void
	renameQuotation: (quotationId: number) => void
	editQuotation: (quotationId: number) => void
	loadingQuotationId: number | null
	isNoQuotations: boolean
	userIsNotFileOwner: boolean
}

export const TableQuotations = ({
	onDownload,
	onPreview,
	createQuotation,
	isNoQuotations,
	userIsNotFileOwner,
	renameQuotation,
	editQuotation,
	loadingQuotationId,
}: Props) => {
	const [searchParams, setSearchParams] = useSearchParams()
	const drawingId = Number(searchParams.get("drawingID"))
	const quotationIdFromUrl = searchParams.get("quotationID")

	const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([])

	const { data: quotations, isPending: isQuotationsPending } = useGetQuotations(drawingId)
	const columns = TableQuotationsColumns(userIsNotFileOwner, renameQuotation, editQuotation, loadingQuotationId)
	const expandable = TableQuotationsExpandable(onDownload, onPreview, setSearchParams, searchParams)

	// Sync expanded row with URL quotationID parameter
	useEffect(() => {
		if (quotationIdFromUrl) {
			setExpandedRowKeys([Number(quotationIdFromUrl)])
		} else {
			setExpandedRowKeys([])
		}
	}, [quotationIdFromUrl])

	const tableData = quotations?.map((quotation) => ({
		key: quotation.id,
		createdAt: quotation.creation_datetime,
		source: quotation.source,
		status: quotation.quotations_state,
		submittedAt: quotation.submitted_datetime,
		files: quotation.files,
		name: quotation.name,
	}))

	const onRowTableHandle = () => ({
		onMouseEnter: () => {
			document.body.style.cursor = "pointer"
		},
		onMouseLeave: () => {
			document.body.style.cursor = "default"
		},
	})

	return (
		<>
			{isNoQuotations ? (
				<NoQuotations createQuotation={createQuotation} userIsNotFileOwner={userIsNotFileOwner} />
			) : isQuotationsPending ? (
				<Skeleton rows={12} />
			) : (
				<TableExpandable<TQuotationTableData>
					className={tableQuotations}
					columns={columns}
					onRow={onRowTableHandle}
					dataSource={tableData}
					expandable={{
						...expandable,
						expandedRowKeys,
						onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
					}}
				/>
			)}
		</>
	)
}
